import { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  Skeleton,
  Typography,
} from "@mui/material";
import useConversationList from "src/_modules/AiSupportChat/hooks/useConversationList";
import ConversationListItem, {
  ROW_SELECTOR,
} from "src/_modules/AiSupportChat/components/ConversationListItem";

const SKELETON_ROWS = 4;

/** Placeholder rows while page 1 loads; hidden from screen readers. */
const SkeletonRows = () => (
  <Box aria-hidden="true">
    {Array.from({ length: SKELETON_ROWS }, (_, index) => (
      <Box
        key={index}
        sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1 }}
      >
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="75%" />
          <Skeleton variant="text" width="30%" sx={{ fontSize: "0.75rem" }} />
        </Box>
        <Skeleton variant="rounded" width={52} height={24} />
      </Box>
    ))}
  </Box>
);

/** An error line with a Retry action. */
const RetryAlert = ({ actionRef, onRetry }) => {
  const { t } = useTranslation("aiSupport");
  return (
    <Alert
      severity="error"
      variant="outlined"
      action={
        <Button
          ref={actionRef}
          color="inherit"
          size="small"
          onClick={onRetry}
          sx={{ textTransform: "none" }}
        >
          {t("message.retry")}
        </Button>
      }
      sx={{ alignItems: "center" }}
    >
      {t("errors.loadHistory")}
    </Alert>
  );
};

/**
 * "Your previous conversations" (plan §3.5): heading, retention caption, and
 * the paginated list with its loading (skeleton), empty and error (Retry)
 * states. The open conversation is pinned first.
 *
 * While an answer streams (`busy`), every row except the live conversation
 * is disabled: opening another one would replace the chat state mid-stream
 * (C17, Q6).
 *
 * @param {object} props
 * @param {number} props.retentionDays
 * @param {(id: string) => void} props.onOpen
 * @param {boolean} [props.busy]
 * @param {string | null} [props.liveConversationId]
 */
export default function ConversationList({
  retentionDays,
  onOpen,
  busy = false,
  liveConversationId = null,
}) {
  const { t } = useTranslation("aiSupport");
  const headingId = useId();
  const {
    conversations,
    isLoading,
    isError,
    retry,
    hasMore,
    loadMore,
    isLoadingMore,
    loadMoreFailed,
  } = useConversationList();

  const headingRef = useRef(null);
  const listRef = useRef(null);
  const actionRef = useRef(null);
  const focusRowAfterLoad = useRef(null);

  // Retry and "Load more" unmount once they succeed. Unless the user moved
  // on, focus goes to the first new row (or back to the action / heading),
  // never to <body>, where Esc no longer reaches the panel (D37).
  useEffect(() => {
    const row = focusRowAfterLoad.current;
    if (row === null || isLoading || isLoadingMore) return;
    focusRowAfterLoad.current = null;

    const active = document.activeElement;
    if (active && active !== document.body && active !== actionRef.current) {
      return;
    }
    // Nothing new (a failure, or only duplicates): the Retry / "Load more"
    // button if there is one, else the last row, else the heading
    const rows = listRef.current?.querySelectorAll(ROW_SELECTOR) ?? [];
    const target =
      rows[row] ??
      actionRef.current ??
      rows[rows.length - 1] ??
      headingRef.current;
    target?.focus();
  }, [isLoading, isLoadingMore, conversations.length]);

  const handleRetry = () => {
    focusRowAfterLoad.current = 0;
    retry();
  };

  const handleLoadMore = () => {
    if (isLoadingMore) return;
    focusRowAfterLoad.current = conversations.length;
    loadMore();
  };

  // One clock for every row of this render
  const now = Date.now();

  let content;
  if (isLoading) {
    content = <SkeletonRows />;
  } else if (isError) {
    content = <RetryAlert actionRef={actionRef} onRetry={handleRetry} />;
  } else if (conversations.length === 0) {
    content = (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        {t("home.historyEmpty")}
      </Typography>
    );
  } else {
    content = (
      <List ref={listRef} disablePadding aria-labelledby={headingId}>
        {conversations.map((conversation) => (
          <ConversationListItem
            key={conversation.id}
            conversation={conversation}
            onOpen={onOpen}
            now={now}
            disabled={busy && conversation.id !== liveConversationId}
          />
        ))}
      </List>
    );
  }

  return (
    <Box>
      <Typography
        id={headingId}
        ref={headingRef}
        tabIndex={-1}
        component="h3"
        variant="subtitle2"
        sx={{ fontWeight: 600, outline: "none" }}
      >
        {t("home.historyTitle")}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        component="p"
        sx={{ mb: 1 }}
      >
        {t("home.retentionNote", { days: retentionDays })}
      </Typography>
      <Box aria-busy={isLoading || isLoadingMore}>
        {content}
        {loadMoreFailed ? (
          <Box sx={{ mt: 1 }}>
            <RetryAlert actionRef={actionRef} onRetry={handleLoadMore} />
          </Box>
        ) : (
          hasMore && (
            // Not `disabled` while loading: a focused button that becomes
            // disabled drops focus to <body>
            <Button
              ref={actionRef}
              fullWidth
              size="small"
              aria-disabled={isLoadingMore}
              onClick={handleLoadMore}
              startIcon={
                isLoadingMore ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
              sx={{ mt: 1, textTransform: "none" }}
            >
              {t("home.loadMore")}
            </Button>
          )
        )}
      </Box>
    </Box>
  );
}
